/**
 * /admin/reels/videos — manage reel uploads + curated playlist.
 * Upload (Mux), list, add/remove/reorder, save to private_reels_config.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { MuxVideoPicker } from "@/components/admin/MuxVideoPicker";
import { MuxFeedUploader } from "@/components/admin/MuxFeedUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  BRAND_MUX_CREATOR_ID,
  BRAND_MUX_EXTERNAL_ID_FEED,
  BRAND_SOURCE_KEY,
} from "@/lib/brandConfig";
import { isValidMuxPlaybackId } from "@/lib/internationalHomeTemplate";
import {
  ArrowDown,
  ArrowUp,
  Copy,
  Film,
  Flame,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { PlaybackSource, PrivateReelsConfig } from "./types";

function parseIds(text: string): string[] {
  return text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => isValidMuxPlaybackId(s));
}

export default function PrivateReelsVideosPage() {
  const { getAuthHeaders, isLoaded, isSignedIn } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<PrivateReelsConfig | null>(null);
  const [resolvedIds, setResolvedIds] = useState<string[]>([]);
  const [curatedText, setCuratedText] = useState("");
  const [pickerId, setPickerId] = useState("");
  const [showUploader, setShowUploader] = useState(false);
  const curatedTextRef = useRef(curatedText);
  const configRef = useRef(config);
  curatedTextRef.current = curatedText;
  configRef.current = config;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/private-reels", { headers });
      const data = (await res.json()) as {
        ok?: boolean;
        config?: PrivateReelsConfig;
        resolvedPlaybackIds?: string[];
        error?: string;
      };
      if (!res.ok || !data.ok || !data.config) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setConfig(data.config);
      setResolvedIds(data.resolvedPlaybackIds || []);
      setCuratedText((data.config.curated_playback_ids || []).join("\n"));
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Error al cargar",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, toast]);

  useEffect(() => {
    if (isLoaded && isSignedIn) void load();
  }, [isLoaded, isSignedIn, load]);

  // Mount Mux Uploader after short delay (heavy web component)
  useEffect(() => {
    const t = window.setTimeout(() => setShowUploader(true), 80);
    return () => window.clearTimeout(t);
  }, []);

  const persistPlaylist = useCallback(
    async (
      nextIds: string[],
      opts?: { source?: PlaybackSource; toastOk?: string },
    ) => {
      const cfg = configRef.current;
      setSaving(true);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch("/api/admin/private-reels", {
          method: "PUT",
          headers,
          body: JSON.stringify({
            enabled: cfg?.enabled ?? true,
            access_mode: cfg?.access_mode ?? "password_or_link",
            playback_source: opts?.source ?? "curated",
            curated_playback_ids: nextIds,
            max_videos_in_feed: cfg?.max_videos_in_feed ?? 50,
            max_views_per_session: cfg?.max_views_per_session ?? 0,
          }),
        });
        const data = (await res.json()) as {
          ok?: boolean;
          config?: PrivateReelsConfig;
          resolvedPlaybackIds?: string[];
          error?: string;
        };
        if (!res.ok || !data.ok) {
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        if (data.config) setConfig(data.config);
        setResolvedIds(data.resolvedPlaybackIds || nextIds);
        const saved = data.config?.curated_playback_ids || nextIds;
        curatedTextRef.current = saved.join("\n");
        setCuratedText(saved.join("\n"));
        if (opts?.toastOk) {
          toast({ title: opts.toastOk, description: `${saved.length} video(s)` });
        }
        return true;
      } catch (e) {
        toast({
          title: "Error al guardar playlist",
          description: e instanceof Error ? e.message : "Error",
          variant: "destructive",
        });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [getAuthHeaders, toast],
  );

  const save = async () => {
    if (!config) return;
    await persistPlaylist(parseIds(curatedText), {
      source: config.playback_source,
      toastOk: "Playlist guardada",
    });
  };

  const addIds = useCallback(
    async (ids: string[], toastOk?: string) => {
      const valid = ids.filter((id) => isValidMuxPlaybackId(id));
      if (valid.length === 0) {
        toast({ title: "Playback ID inválido", variant: "destructive" });
        return;
      }
      const list = parseIds(curatedTextRef.current);
      const next = [...list];
      for (const id of valid) {
        if (!next.includes(id)) next.push(id);
      }
      curatedTextRef.current = next.join("\n");
      setCuratedText(next.join("\n"));
      setConfig((c) =>
        c ? { ...c, playback_source: "curated" as PlaybackSource } : c,
      );
      await persistPlaylist(next, {
        source: "curated",
        toastOk: toastOk || "Agregado a playlist",
      });
    },
    [persistPlaylist, toast],
  );

  const removeId = async (id: string) => {
    const next = parseIds(curatedTextRef.current).filter((x) => x !== id);
    curatedTextRef.current = next.join("\n");
    setCuratedText(next.join("\n"));
    setConfig((c) =>
      c ? { ...c, playback_source: "curated" as PlaybackSource } : c,
    );
    await persistPlaylist(next, {
      source: "curated",
      toastOk: "Quitado de playlist",
    });
  };

  const moveId = async (id: string, dir: -1 | 1) => {
    const list = parseIds(curatedTextRef.current);
    const i = list.indexOf(id);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    curatedTextRef.current = next.join("\n");
    setCuratedText(next.join("\n"));
    setConfig((c) =>
      c ? { ...c, playback_source: "curated" as PlaybackSource } : c,
    );
    await persistPlaylist(next, { source: "curated" });
  };

  const addFromPicker = () => {
    void addIds([pickerId.trim()], "Agregado desde biblioteca");
    setPickerId("");
  };

  const onUploadReady = useCallback(
    async (playbackId: string, meta: { external_id: string | null }) => {
      const isFeed =
        (meta.external_id || BRAND_MUX_EXTERNAL_ID_FEED) ===
        BRAND_MUX_EXTERNAL_ID_FEED;
      await addIds(
        [playbackId],
        isFeed
          ? "Video subido y en playlist de reels"
          : "Video listo — agregado a playlist",
      );
    },
    [addIds],
  );

  const startEmptyCurated = async () => {
    curatedTextRef.current = "";
    setCuratedText("");
    await persistPlaylist([], {
      source: "curated",
      toastOk: "Playlist vacía",
    });
  };

  const playlistIds = useMemo(() => parseIds(curatedText), [curatedText]);
  const displayIds =
    config?.playback_source === "curated" ? playlistIds : resolvedIds;

  if (!isLoaded) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando sesión…
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">
        Iniciá sesión de admin (Clerk) para gestionar uploads de reels.
      </div>
    );
  }

  if (loading || !config) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando videos…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Playlist manager ─────────────────────────────────────────── */}
      <section className="rounded-2xl border border-violet-500/25 bg-zinc-900/50 p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Film className="h-4 w-4 text-violet-300" /> Gestionar reels
            </h2>
            <p className="mt-1 text-[11px] text-muted-foreground max-w-xl">
              Subí, ordená y quitá videos del feed privado. Cada upload usa{" "}
              <code className="text-[10px]">
                creator_id={BRAND_MUX_CREATOR_ID}
              </code>
              ,{" "}
              <code className="text-[10px]">
                external_id={BRAND_MUX_EXTERNAL_ID_FEED}
              </code>
              . Luego podés compartirlos en{" "}
              <Link
                className="underline"
                to="/admin/reels/secret-share"
              >
                Secret share
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="font-mono text-[10px]">
              {displayIds.length} en feed
            </Badge>
            <Badge variant="outline" className="font-mono text-[10px]">
              brand={BRAND_SOURCE_KEY}
            </Badge>
            <Badge variant="outline" className="font-mono text-[10px]">
              source={config.playback_source}
            </Badge>
          </div>
        </div>

        {displayIds.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 bg-black/25 px-4 py-6 text-center space-y-2">
            <p className="text-sm text-white/80">Sin videos en reels</p>
            <p className="text-[11px] text-muted-foreground max-w-md mx-auto">
              Subí el primero con el uploader de abajo. Se agrega solo a la
              playlist curada cuando Mux termina de procesar.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-xs">
              Playlist ({displayIds.length}) — quitar / reordenar / copiar ID
            </Label>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {displayIds.map((id, idx) => (
                <div
                  key={id}
                  className="flex gap-2 rounded-xl border border-white/10 bg-black/30 p-2"
                >
                  <img
                    src={`https://image.mux.com/${id}/thumbnail.jpg?width=120&height=160&fit_mode=smartcrop&time=1`}
                    alt=""
                    className="h-20 w-14 shrink-0 rounded-lg object-cover ring-1 ring-white/10"
                    loading="lazy"
                  />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-[9px] font-mono">
                        #{idx + 1}
                      </Badge>
                      {config.playback_source === "curated" && (
                        <span className="text-[9px] text-muted-foreground">
                          curado
                        </span>
                      )}
                    </div>
                    <p
                      className="font-mono text-[9px] text-muted-foreground truncate"
                      title={id}
                    >
                      {id}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        title="Subir"
                        disabled={
                          saving ||
                          config.playback_source !== "curated" ||
                          idx === 0
                        }
                        onClick={() => void moveId(id, -1)}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        title="Bajar"
                        disabled={
                          saving ||
                          config.playback_source !== "curated" ||
                          idx >= displayIds.length - 1
                        }
                        onClick={() => void moveId(id, 1)}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        title="Copiar playback ID"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(id);
                            toast({ title: "Playback ID copiado" });
                          } catch {
                            toast({
                              title: "No se pudo copiar",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-300 hover:text-red-200"
                        title="Quitar de playlist"
                        disabled={
                          saving || config.playback_source !== "curated"
                        }
                        onClick={() => void removeId(id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {config.playback_source !== "curated" && (
              <p className="text-[11px] text-amber-200/90">
                Estás en modo Auto Mux. Para quitar/reordenar, cambiá a{" "}
                <strong>lista curada</strong> (o «Empezar vacío» y volvé a
                subir).
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            asChild
          >
            <Link to="/admin/reels/secret-share">
              <Flame className="h-3.5 w-3.5 mr-1" /> Crear secret share
            </Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void load()}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Recargar
          </Button>
        </div>
      </section>

      {/* ── Upload ───────────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-white/[0.08] bg-zinc-900/40 p-5 space-y-3">
        <h3 className="text-sm font-semibold">1. Subir video (Mux)</h3>
        <p className="text-[11px] text-muted-foreground">
          Arrastrá un archivo o elegilo. Al quedar ready se agrega solo a la
          playlist curada. También podés sumar uploads listos con «+ Playlist».
        </p>
        {showUploader ? (
          <MuxFeedUploader
            onReady={onUploadReady}
            onAddToPlaylist={(id) =>
              void addIds([id], "Agregado desde lista de uploads")
            }
            playlistPlaybackIds={playlistIds}
            deferList={false}
          />
        ) : (
          <div className="rounded-xl border border-dashed border-white/10 px-4 py-6 text-center text-[11px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            Preparando Mux Uploader…
          </div>
        )}
      </section>

      {/* ── Source + advanced ────────────────────────────────────────── */}
      <section className="rounded-2xl border border-white/[0.08] bg-zinc-900/40 p-5 space-y-4">
        <h3 className="text-sm font-semibold">2. Fuente del feed y playlist</h3>

        <div className="space-y-1.5">
          <Label>Cómo arma el feed</Label>
          <Select
            value={config.playback_source}
            onValueChange={(v) =>
              setConfig({ ...config, playback_source: v as PlaybackSource })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="curated">
                Lista curada (recomendado) — gestionás cada video
              </SelectItem>
              <SelectItem value="mux_api">
                Auto Mux — todos creator={BRAND_MUX_CREATOR_ID} + external_id=
                {BRAND_MUX_EXTERNAL_ID_FEED}
              </SelectItem>
            </SelectContent>
          </Select>
          {config.playback_source === "mux_api" && (
            <p className="text-[11px] text-amber-200/90">
              Auto puede incluir el teaser del home. Preferí lista curada para
              gestionar solo lo que subís acá.
            </p>
          )}
        </div>

        <div className="space-y-3 rounded-xl border border-white/[0.06] bg-black/20 p-4">
          <div className="space-y-1.5">
            <Label>Agregar desde biblioteca Mux</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="flex-1">
                <MuxVideoPicker value={pickerId} onChange={setPickerId} />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={addFromPicker}
                disabled={saving}
              >
                <Plus className="h-4 w-4 mr-1" /> Agregar y guardar
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Playback IDs (avanzado, {playlistIds.length})</Label>
            <Textarea
              rows={5}
              className="font-mono text-xs"
              value={curatedText}
              onChange={(e) => setCuratedText(e.target.value)}
              placeholder="uno por línea…"
            />
          </div>
        </div>

        <div className="space-y-1.5 max-w-xs">
          <Label>Máx. videos en el feed (1–100)</Label>
          <Input
            type="number"
            min={1}
            max={100}
            value={config.max_videos_in_feed}
            onChange={(e) =>
              setConfig({
                ...config,
                max_videos_in_feed: Number(e.target.value) || 50,
              })
            }
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => void save()} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Guardar playlist
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={() => void startEmptyCurated()}
          >
            Empezar vacío
          </Button>
          <Button type="button" variant="outline" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4 mr-1" /> Recargar
          </Button>
        </div>
      </section>
    </div>
  );
}
